import { NAKSHATRA_NAMES } from '@astrally/shared';
import { rashiName, signCount, signLord } from '../core/zodiac.js';
import {
  GANA,
  VASHYA,
  VASHYA_SCORE,
  YONI,
  nakshatraCount,
  relation,
  taraFavourable,
  taraOf,
  yoniScore,
  type Gana,
  type MoonProfile,
} from './ashtakoota.js';

/**
 * Dasa Porutham — the South Indian ten-porutham kundli match, offered as the
 * region-adjusted alternative to the North Indian Ashtakoota 36.
 *
 * WHY IT EXISTS SEPARATELY: the two systems disagree on purpose. Ashtakoota
 * weights eight kootas out of 36; the southern reckoning asks ten yes/no
 * questions and treats two of them (rajju, vedha) as blockers rather than as
 * deductions. Running one and relabelling it as the other would misrepresent
 * both traditions.
 *
 * SHARED DATA, NOT SHARED GRADING: every table this file needs already lives in
 * ./ashtakoota.ts — yoni animals and their enemy pairs, gana classes, vasya
 * groups, the naisargika graha friendships, and the inclusive nakshatra count.
 * They are imported, never re-typed here. What differs is the banding: this
 * system grades each porutham 1 / 0.5 / 0 where Ashtakoota grades out of the
 * koota's own maximum. A divergence in the underlying tables would be a bug; a
 * divergence in the banding is the point.
 *
 * DIRECTION: `a` is the BRIDE (girl / vadhu), `b` is the GROOM (boy / vara).
 * Four poruthams are defined directionally and read the pair in that order —
 * dina, mahendra and stree-dirgha count from the bride's star to the groom's,
 * and rasi counts from the bride's rashi to the groom's. Passing the profiles
 * the other way round answers a different question, so callers must not sort
 * their inputs.
 *
 * SCORING: pass = 1, classical middling outcome = 0.5, fail = 0; `pass` is true
 * only for a full 1. `total` is honest arithmetic — a rajju or vedha failure
 * costs exactly its one point and no more, even though the tradition treats it
 * as a veto. That emphasis is carried in the `note` text and is a product-copy
 * decision; the engine does not secretly zero the total.
 *
 * Interpretive lens only. Classical sources attach severe literal outcomes to
 * some of these (rajju in particular); those readings are deliberately not
 * reproduced in the notes.
 */

export type PoruthamScore = 0 | 0.5 | 1;

export type PoruthamId =
  | 'dina'
  | 'gana'
  | 'mahendra'
  | 'streeDirgha'
  | 'yoni'
  | 'rasi'
  | 'rasyadhipati'
  | 'vasya'
  | 'rajju'
  | 'vedha';

export interface PoruthamEntry {
  id: PoruthamId;
  name: string;
  score: PoruthamScore;
  /** True only for a full match; a 0.5 middling outcome is not a pass. */
  pass: boolean;
  note: string;
}

export interface PoruthamResult {
  total: number;
  max: 10;
  poruthams: PoruthamEntry[];
}

// --- Rajju -----------------------------------------------------------------
/**
 * The five rajju (limb) groups over the 27 nakshatras.
 *
 * Convention: the standard zig-zag laid out in the Tamil/Kerala match tables —
 * pada, kati, nabhi, kanta, siro and back down again, repeating every nine
 * stars. Pada, kati, nabhi and kanta therefore hold six nakshatras each and
 * siro (the turning point of the zig-zag) holds three: 6+6+6+6+3 = 27, each
 * nakshatra in exactly one group. Kati is also called uru and nabhi is also
 * called udara in some texts; the membership is the same.
 *
 * Indices are 0-based (0 = Ashwini), matching NAKSHATRA_NAMES.
 */
export type RajjuGroup = 'pada' | 'kati' | 'nabhi' | 'kanta' | 'siro';

export const RAJJU_GROUPS: Record<RajjuGroup, readonly number[]> = {
  // Ashwini, Ashlesha, Magha, Jyeshtha, Mula, Revati
  pada: [0, 8, 9, 17, 18, 26],
  // Bharani, Pushya, Purva Phalguni, Anuradha, Purva Ashadha, Uttara Bhadrapada
  kati: [1, 7, 10, 16, 19, 25],
  // Krittika, Punarvasu, Uttara Phalguni, Vishakha, Uttara Ashadha, Purva Bhadrapada
  nabhi: [2, 6, 11, 15, 20, 24],
  // Rohini, Ardra, Hasta, Swati, Shravana, Shatabhisha
  kanta: [3, 5, 12, 14, 21, 23],
  // Mrigashira, Chitra, Dhanishta
  siro: [4, 13, 22],
};

const RAJJU_LABELS: Record<RajjuGroup, string> = {
  pada: 'pada (feet)',
  kati: 'kati (waist)',
  nabhi: 'nabhi (navel)',
  kanta: 'kanta (throat)',
  siro: 'siro (head)',
};

const RAJJU_OF: RajjuGroup[] = buildRajjuLookup();

function buildRajjuLookup(): RajjuGroup[] {
  const lookup = new Array<RajjuGroup | undefined>(27).fill(undefined);
  for (const [group, members] of Object.entries(RAJJU_GROUPS) as [RajjuGroup, readonly number[]][]) {
    for (const n of members) {
      if (n < 0 || n > 26) throw new Error(`RAJJU_GROUPS: nakshatra index ${n} out of range`);
      if (lookup[n] !== undefined) throw new Error(`RAJJU_GROUPS: nakshatra index ${n} listed twice`);
      lookup[n] = group;
    }
  }
  const missing = lookup.findIndex((g) => g === undefined);
  if (missing !== -1) throw new Error(`RAJJU_GROUPS: nakshatra index ${missing} has no rajju`);
  return lookup as RajjuGroup[];
}

/** Rajju group of a 0-based nakshatra index. */
export function rajjuOf(nakshatra: number): RajjuGroup {
  return RAJJU_OF[((nakshatra % 27) + 27) % 27];
}

// --- Vedha -----------------------------------------------------------------
/**
 * The classical vedha (mutual obstruction) nakshatra pairs, 0-based.
 *
 * Convention: the thirteen pairs of the standard vedha table. They fall into
 * three runs that are each symmetric about a centre — Ashwini..Rohini against
 * Jyeshtha..Swati, Mrigashira..Ashlesha against Dhanishta..Mula, and
 * Magha..Hasta against Revati..Shatabhisha. Twenty-seven is odd, so exactly one
 * nakshatra is left without a vedha partner: Chitra (index 13). That is a
 * property of the table, not an omission — the test asserts it.
 *
 * The relation is mutual, so each pair is listed once and looked up both ways.
 */
export const VEDHA_PAIRS: readonly (readonly [number, number])[] = [
  [0, 17], // Ashwini – Jyeshtha
  [1, 16], // Bharani – Anuradha
  [2, 15], // Krittika – Vishakha
  [3, 14], // Rohini – Swati
  [4, 22], // Mrigashira – Dhanishta
  [5, 21], // Ardra – Shravana
  [6, 20], // Punarvasu – Uttara Ashadha
  [7, 19], // Pushya – Purva Ashadha
  [8, 18], // Ashlesha – Mula
  [9, 26], // Magha – Revati
  [10, 25], // Purva Phalguni – Uttara Bhadrapada
  [11, 24], // Uttara Phalguni – Purva Bhadrapada
  [12, 23], // Hasta – Shatabhisha
];

const VEDHA_OF: (number | undefined)[] = buildVedhaLookup();

function buildVedhaLookup(): (number | undefined)[] {
  const lookup = new Array<number | undefined>(27).fill(undefined);
  for (const [x, y] of VEDHA_PAIRS) {
    if (x < 0 || x > 26 || y < 0 || y > 26) throw new Error(`VEDHA_PAIRS: index out of range (${x},${y})`);
    if (x === y) throw new Error(`VEDHA_PAIRS: nakshatra ${x} paired with itself`);
    if (lookup[x] !== undefined || lookup[y] !== undefined) {
      throw new Error(`VEDHA_PAIRS: nakshatra already paired (${x},${y})`);
    }
    lookup[x] = y;
    lookup[y] = x;
  }
  return lookup;
}

/** True when the two stars form a classical vedha pair. */
export function hasVedha(nakA: number, nakB: number): boolean {
  return VEDHA_OF[((nakA % 27) + 27) % 27] === ((nakB % 27) + 27) % 27;
}

// --- Mahendra --------------------------------------------------------------
/**
 * Mahendra porutham: the groom's star counted from the bride's must land on one
 * of these inclusive positions (every third star from the 4th, the classical
 * 4-7-10-13-16-19-22-25 series).
 *
 * Worth knowing: 27 stars make the reverse count 29 - n, and the series is
 * closed under that map (4↔25, 7↔22, 10↔19, 13↔16). So the SCORE happens to
 * come out the same if the roles are swapped even though the porutham is
 * defined directionally — the note still reports the bride-to-groom count.
 */
export const MAHENDRA_POSITIONS: readonly number[] = [4, 7, 10, 13, 16, 19, 22, 25];

/**
 * Stree-dirgha porutham: the groom's star must be far enough ahead of the
 * bride's. Convention used here is the strict one — the inclusive count from
 * the bride's star to the groom's must be GREATER THAN 13, i.e. beyond the
 * half-way point of the 27. Several almanacs accept a count above 9 as a
 * lesser match; we do not grade that middle band, so a count of 10-13 scores 0.
 */
export const STREE_DIRGHA_MIN_COUNT = 13;

// --- Gana ------------------------------------------------------------------
/**
 * Gana porutham bands the shared GANA table (imported, not redefined):
 *   same gana                → 1
 *   deva with manushya       → 0.5 (the classical middling outcome)
 *   anything with rakshasa   → 0
 * Some texts grade this directionally (a manushya groom with a deva bride
 * ranking below the reverse). We use the symmetric grid and say so rather than
 * inventing a direction the shared table cannot support.
 */
function ganaScore(bride: Gana, groom: Gana): PoruthamScore {
  if (bride === groom) return 1;
  const pair = [bride, groom].sort().join('-');
  return pair === 'deva-manushya' ? 0.5 : 0;
}

// --- Rasi ------------------------------------------------------------------
/**
 * Rasi porutham, counting the groom's rashi from the bride's rashi (1-12):
 *   6 or 8  → 0    shashtashtaka, the classical rashi defect (both directions
 *                  of the same 6-8 relation)
 *   2 or 12 → 0.5  dwirdwadasa, a softened defect
 *   1       → 1 when the nakshatras differ, 0.5 when the couple shares one star
 *   else    → 1
 *
 * Note the deliberate difference from Ashtakoota's bhakoot, which also zeroes
 * the 5-9 relation: the southern reading treats nava-pancham as auspicious, so
 * counts 5 and 9 score a full point here. Same counting helper (`signCount`),
 * different grading — see the file header.
 */
function rasiScore(bride: MoonProfile, groom: MoonProfile): { score: PoruthamScore; label: string } {
  const count = signCount(bride.rashi, groom.rashi);
  if (count === 6 || count === 8) return { score: 0, label: 'shashtashtaka (6-8), the classical rashi defect' };
  if (count === 2 || count === 12) return { score: 0.5, label: 'dwirdwadasa (2-12), a softened defect' };
  if (count === 1) {
    return bride.nakshatra === groom.nakshatra
      ? { score: 0.5, label: 'one rashi and one nakshatra — held to be middling' }
      : { score: 1, label: 'one rashi, different nakshatras — favoured' };
  }
  return { score: 1, label: 'a supportive rashi relation' };
}

// --- Rasyadhipati ----------------------------------------------------------
/**
 * Rasyadhipati porutham: the naisargika friendship of the two rashi lords,
 * banded off the shared `relation()` (imported from ashtakoota.ts):
 *   same graha, or friends both ways → 1
 *   friend/neutral or neutral both ways → 0.5
 *   either graha reads the other as an enemy → 0
 */
function rasyadhipatiScore(bride: MoonProfile, groom: MoonProfile): { score: PoruthamScore; label: string } {
  const lordA = signLord(bride.rashi);
  const lordB = signLord(groom.rashi);
  if (lordA === lordB) {
    return { score: 1, label: `both rashis answer to ${lordA}` };
  }
  const ab = relation(lordA, lordB);
  const ba = relation(lordB, lordA);
  if (ab === 'enemy' || ba === 'enemy') {
    return { score: 0, label: `${lordA} and ${lordB} read each other coolly` };
  }
  if (ab === 'friend' && ba === 'friend') {
    return { score: 1, label: `${lordA} and ${lordB} are mutual friends` };
  }
  return { score: 0.5, label: `${lordA} and ${lordB} are friendly-to-neutral` };
}

// --- Vasya -----------------------------------------------------------------
/**
 * Vasya porutham bands the shared VASHYA group grid. Ashtakoota averages the
 * two directions out of 2; the same average is banded here as 2 → 1, anything
 * above 0 → 0.5, 0 → 0 (the vanachara pairings the grid treats as unyielding).
 */
function vasyaScore(bride: MoonProfile, groom: MoonProfile): PoruthamScore {
  const gb = VASHYA[bride.rashi];
  const gg = VASHYA[groom.rashi];
  const mean = (VASHYA_SCORE[gb][gg] + VASHYA_SCORE[gg][gb]) / 2;
  if (mean >= 2) return 1;
  return mean > 0 ? 0.5 : 0;
}

// --- Public API ------------------------------------------------------------

/**
 * South Indian ten-porutham match.
 *
 * @param a the BRIDE's Moon profile (rashi 0-11, nakshatra 0-26)
 * @param b the GROOM's Moon profile
 */
export function computePorutham(a: MoonProfile, b: MoonProfile): PoruthamResult {
  const brideStar = NAKSHATRA_NAMES[a.nakshatra];
  const groomStar = NAKSHATRA_NAMES[b.nakshatra];
  const count = nakshatraCount(a.nakshatra, b.nakshatra); // bride → groom, inclusive
  const entries: { id: PoruthamId; name: string; score: PoruthamScore; note: string }[] = [];

  // 1. Dina — the tara reckoned from the bride's star to the groom's.
  const dinaOk = taraFavourable(a.nakshatra, b.nakshatra);
  entries.push({
    id: 'dina',
    name: 'Dina porutham',
    score: dinaOk ? 1 : 0,
    note: `Groom's ${groomStar} is ${count} from the bride's ${brideStar}, tara ${taraOf(a.nakshatra, b.nakshatra)} — ${
      dinaOk ? 'a supportive count for day-to-day life together' : 'one of the strained taras (3, 5, 7)'
    }.`,
  });

  // 2. Gana — temperament classes of the two stars.
  const ganaB = GANA[a.nakshatra];
  const ganaG = GANA[b.nakshatra];
  const gana = ganaScore(ganaB, ganaG);
  entries.push({
    id: 'gana',
    name: 'Gana porutham',
    score: gana,
    note: `Ganas ${ganaB} (bride) and ${ganaG} (groom) — ${
      gana === 1 ? 'one temperament class' : gana === 0.5 ? 'the classical middling pairing' : 'classes that pull apart'
    }.`,
  });

  // 3. Mahendra — the groom's star on a 4/7/10/... position from the bride's.
  const mahendraOk = MAHENDRA_POSITIONS.includes(count);
  entries.push({
    id: 'mahendra',
    name: 'Mahendra porutham',
    score: mahendraOk ? 1 : 0,
    note: `Groom's ${groomStar} is ${count} from the bride's ${brideStar}; mahendra looks for ${MAHENDRA_POSITIONS.join(
      ', ',
    )}.`,
  });

  // 4. Stree-dirgha — the groom's star well ahead of the bride's (>13).
  const streeOk = count > STREE_DIRGHA_MIN_COUNT;
  entries.push({
    id: 'streeDirgha',
    name: 'Stree-dirgha porutham',
    score: streeOk ? 1 : 0,
    note: `Count ${count} from the bride's ${brideStar} to the groom's ${groomStar}; this reading needs more than ${STREE_DIRGHA_MIN_COUNT}, so it ${
      streeOk ? 'holds' : 'does not hold'
    }.`,
  });

  // 5. Yoni — shared animal table; 4/2/0 out of Ashtakoota banded to 1/0.5/0.
  const yoniRaw = yoniScore(YONI[a.nakshatra], YONI[b.nakshatra]);
  const yoni = (yoniRaw / 4) as PoruthamScore;
  entries.push({
    id: 'yoni',
    name: 'Yoni porutham',
    score: yoni,
    note: `Yoni animals ${YONI[a.nakshatra]} (bride) and ${YONI[b.nakshatra]} (groom) — ${
      yoni === 1 ? 'the same yoni' : yoni === 0.5 ? 'neither matched nor opposed' : 'a classical opposed pair'
    }.`,
  });

  // 6. Rasi — the groom's rashi counted from the bride's.
  const rasi = rasiScore(a, b);
  entries.push({
    id: 'rasi',
    name: 'Rasi porutham',
    score: rasi.score,
    note: `Groom's ${rashiName(b.rashi)} is ${signCount(a.rashi, b.rashi)} from the bride's ${rashiName(
      a.rashi,
    )} — ${rasi.label}.`,
  });

  // 7. Rasyadhipati — the two rashi lords.
  const adhipati = rasyadhipatiScore(a, b);
  entries.push({
    id: 'rasyadhipati',
    name: 'Rasyadhipati porutham',
    score: adhipati.score,
    note: `Rashi lords ${signLord(a.rashi)} (bride) and ${signLord(b.rashi)} (groom) — ${adhipati.label}.`,
  });

  // 8. Vasya — mutual sway of the two rashis.
  const vasya = vasyaScore(a, b);
  entries.push({
    id: 'vasya',
    name: 'Vasya porutham',
    score: vasya,
    note: `Vasya groups ${VASHYA[a.rashi]} (bride) and ${VASHYA[b.rashi]} (groom) — ${
      vasya === 1 ? 'they hold each other easily' : vasya === 0.5 ? 'partial sway' : 'little natural sway'
    }.`,
  });

  // 9. Rajju — the veto porutham. Same limb group = fail.
  const rajjuB = rajjuOf(a.nakshatra);
  const rajjuG = rajjuOf(b.nakshatra);
  const rajjuSame = rajjuB === rajjuG;
  entries.push({
    id: 'rajju',
    name: 'Rajju porutham',
    score: rajjuSame ? 0 : 1,
    note: rajjuSame
      ? `RAJJU DOSHA — traditionally treated as a veto, not a deduction: both stars sit in the ${RAJJU_LABELS[rajjuB]} rajju. Weigh this against the whole kundli rather than the score alone.`
      : `Different rajju groups — ${RAJJU_LABELS[rajjuB]} (bride) and ${RAJJU_LABELS[rajjuG]} (groom).`,
  });

  // 10. Vedha — the second veto porutham.
  const vedha = hasVedha(a.nakshatra, b.nakshatra);
  entries.push({
    id: 'vedha',
    name: 'Vedha porutham',
    score: vedha ? 0 : 1,
    note: vedha
      ? `VEDHA DOSHA — traditionally treated as a veto, not a deduction: ${brideStar} and ${groomStar} are a classical vedha (obstruction) pair. Weigh this against the whole kundli rather than the score alone.`
      : `${brideStar} and ${groomStar} carry no vedha between them.`,
  });

  const poruthams: PoruthamEntry[] = entries.map((e) => ({ ...e, pass: e.score === 1 }));
  const total = poruthams.reduce((s, p) => s + p.score, 0);
  return { total, max: 10, poruthams };
}
